import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { useActiveTenant } from '../contexts/tenant'
import { usePermissions } from '../contexts/permissions'

type Snap = {
  snapshotDate: string; fracUnits: number; armUnits: number; armBoxes: number; totalUnits: number; source: string
}
type Prod = { id: string; sku: string; name: string }
type DayRow = { productId: string; sku: string | null; name: string | null; fracUnits: number; armUnits: number; armBoxes: number; totalUnits: number; source: string }

const ymd = (d: Date) => d.toISOString().slice(0, 10)

export function PosicaoEstoquePage() {
  const tenantId = useActiveTenant()
  const { can } = usePermissions()
  const qc = useQueryClient()
  const canEdit = can('posicao-estoque', 'edit')
  const [from, setFrom] = useState(ymd(new Date(Date.now() - 29 * 86400000)))
  const [to, setTo] = useState(ymd(new Date()))
  const [productId, setProductId] = useState('')
  const [msg, setMsg] = useState<string | null>(null)

  const { data: products = [] } = useQuery<Prod[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: series = [], isLoading, refetch } = useQuery<Snap[]>({
    queryKey: ['stock', 'position', tenantId, from, to, productId],
    queryFn: async () => (await api.get('/stock/position', { params: { tenantId, from, to, ...(productId ? { productId } : {}) } })).data,
    enabled: !!tenantId,
  })
  const prodSel = products.find(p => p.id === productId)

  const snapshotNow = useMutation({
    mutationFn: async () => (await api.post('/stock/position/snapshot', { tenantId })).data,
    onSuccess: () => { setMsg('Snapshot de hoje registrado.'); qc.invalidateQueries({ queryKey: ['stock', 'position'] }) },
    onError: (e: any) => setMsg(e?.response?.data?.title ?? 'Falha ao registrar snapshot'),
  })
  const backfill = useMutation({
    mutationFn: async () => (await api.post('/stock/position/backfill', { tenantId, from })).data,
    onSuccess: (d: any) => { setMsg(`Histórico reconstruído: ${d?.days ?? 0} dia(s) desde ${d?.from ?? from}.`); qc.invalidateQueries({ queryKey: ['stock', 'position'] }) },
    onError: (e: any) => setMsg(e?.response?.data?.title ?? 'Falha no backfill'),
  })

  const max = Math.max(1, ...series.map(s => s.totalUnits))
  const ult = series[series.length - 1]

  // Posição de todos os produtos num dia específico (tabela estilo Saldos)
  const [dayDate, setDayDate] = useState(ymd(new Date()))
  const [search, setSearch] = useState('')
  const { data: dayRows = [], isLoading: dayLoading } = useQuery<DayRow[]>({
    queryKey: ['stock', 'position', 'day', tenantId, dayDate],
    queryFn: async () => (await api.get('/stock/position/day', { params: { tenantId, date: dayDate } })).data,
    enabled: !!tenantId,
  })
  const term = search.trim().toLowerCase()
  const dayFiltered = dayRows.filter(r => !term || (r.sku ?? '').toLowerCase().includes(term) || (r.name ?? '').toLowerCase().includes(term))
  const dayTot = dayFiltered.reduce((a, r) => ({ frac: a.frac + r.fracUnits, arm: a.arm + r.armUnits, armB: a.armB + r.armBoxes, total: a.total + r.totalUnits }), { frac: 0, arm: 0, armB: 0, total: 0 })

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Posição de estoque (por dia)</h2>
          <p className="text-sm text-gray-500">
            {prodSel ? <>Produto <b>{prodSel.sku} — {prodSel.name}</b>, dia a dia.</> : 'Fracionamento + armazém, dia a dia (agregado).'} Snapshot automático diário + reconstrução por NF/ledger.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <Input label="De" type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-44" />
            <Input label="Até" type="date" value={to} onChange={e => setTo(e.target.value)} className="w-44" />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Produto</label>
              <select value={productId} onChange={e => setProductId(e.target.value)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm min-w-[220px]">
                <option value="">Todos (agregado)</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
              </select>
            </div>
            <Button variant="secondary" onClick={() => refetch()}>Atualizar</Button>
            {canEdit && (
              <>
                <Button variant="secondary" loading={snapshotNow.isPending} onClick={() => { setMsg(null); snapshotNow.mutate() }}>Snapshot agora</Button>
                <Button variant="secondary" loading={backfill.isPending}
                  onClick={() => { if (window.confirm('Reconstruir o histórico do período pelas notas fiscais + ledger? Substitui só as linhas estimadas (não os snapshots reais).')) { setMsg(null); backfill.mutate() } }}>
                  Reconstruir histórico
                </Button>
              </>
            )}
            {msg && <p className="text-xs text-gray-500">{msg}</p>}
          </div>

          {ult && (
            <div className="flex flex-wrap gap-3 pt-1">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-4 py-2">
                <p className="text-xl font-bold text-emerald-700">{ult.fracUnits} <span className="text-xs text-gray-400">un</span></p>
                <p className="text-xs text-gray-500">Fracionamento ({new Date(`${ult.snapshotDate}T00:00:00`).toLocaleDateString('pt-BR')})</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-4 py-2">
                <p className="text-xl font-bold text-gray-900">{ult.armUnits} <span className="text-xs text-gray-400">un · {ult.armBoxes} cx</span></p>
                <p className="text-xs text-gray-500">Armazém / pulmão</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50/40 px-4 py-2">
                <p className="text-xl font-bold text-blue-700">{ult.totalUnits} <span className="text-xs text-blue-300">un</span></p>
                <p className="text-xs text-gray-500">Total em estoque</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><span className="text-sm font-semibold">Evolução diária{prodSel ? ` — ${prodSel.sku}` : ' (todos)'}</span></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="py-8 text-center text-sm text-gray-400">Carregando…</p>
          ) : series.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">Sem dados no período. Use <b>Reconstruir histórico</b> (admin) para preencher a partir das notas.</p>
          ) : (
            <>
              {/* Barras empilhadas: fracionamento (verde) + armazém (cinza) */}
              <div className="space-y-1">
                {series.map(s => (
                  <div key={s.snapshotDate} className="flex items-center gap-2">
                    <span className="w-16 shrink-0 text-xs text-gray-500">{new Date(`${s.snapshotDate}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                    <div className="flex h-3.5 flex-1 overflow-hidden rounded bg-gray-100" title={`Frac ${s.fracUnits} · Arm ${s.armUnits}`}>
                      <div className="h-3.5 bg-emerald-500" style={{ width: `${Math.round((s.fracUnits / max) * 100)}%` }} />
                      <div className="h-3.5 bg-gray-400" style={{ width: `${Math.round((s.armUnits / max) * 100)}%` }} />
                    </div>
                    <span className="w-14 shrink-0 text-right text-xs font-semibold text-gray-700">{s.totalUnits}</span>
                    {s.source === 'backfill' && <span className="w-4 shrink-0 text-amber-500" title="Estimado (backfill)">~</span>}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Fracionamento</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-gray-400" /> Armazém</span>
                <span className="flex items-center gap-1"><span className="text-amber-500">~</span> estimado (backfill NF/ledger)</span>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Dia</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Fracionamento</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Armazém (un · cx)</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Fonte</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[...series].reverse().map(s => (
                      <tr key={s.snapshotDate} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700">{new Date(`${s.snapshotDate}T00:00:00`).toLocaleDateString('pt-BR')}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">{s.fracUnits}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{s.armUnits} · {s.armBoxes} cx</td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900">{s.totalUnits}</td>
                        <td className="px-3 py-2 text-xs text-gray-400">{s.source === 'backfill' ? 'estimado' : 'snapshot'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Posição num dia específico — tabela por produto (estilo Saldos) */}
      <Card>
        <CardHeader><span className="text-sm font-semibold">Posição num dia específico</span></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <Input label="Dia" type="date" value={dayDate} onChange={e => setDayDate(e.target.value)} className="w-44" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar SKU ou produto…"
              className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>

          {dayLoading ? (
            <p className="py-8 text-center text-sm text-gray-400">Carregando…</p>
          ) : dayRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">Sem posição registrada nesse dia. Use <b>Reconstruir histórico</b> (admin) ou aguarde o snapshot diário.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Fracionamento</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Armazém (un · cx)</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dayFiltered.map(r => (
                    <tr key={r.productId} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono font-medium text-gray-900">{r.sku ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{r.name ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">{r.fracUnits}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{r.armUnits} · {r.armBoxes} cx</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{r.totalUnits}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td className="px-3 py-2 font-semibold text-gray-900" colSpan={2}>Total ({dayFiltered.length} SKUs)</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-700">{dayTot.frac}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-700">{dayTot.arm} · {dayTot.armB} cx</td>
                    <td className="px-3 py-2 text-right font-bold text-blue-700">{dayTot.total}</td>
                  </tr>
                </tbody>
              </table>
              {dayRows[0]?.source === 'backfill' && <p className="mt-2 text-xs text-amber-500">~ Posição estimada (backfill NF/ledger) — dias com snapshot real são exatos.</p>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
